import { useUser } from '@clerk/clerk-react';
import { Loader2, MapPin, UserIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { SAMPLE_ZEE_USERS } from '@/constants/territories/dummy';
import { US_STATES } from '@/constants/territories/states';
import { TERRITORIES_TOUR_CLASSES } from '@/constants/tour/classNames';
import { getStateUserCount } from '@/lib/territories';
import { cn } from '@/lib/ui/utils';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { Territory } from '@/types/territories/territory.type';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { StateStatusBadge } from './StateStatusBadge';
import { TerritoryItem } from './territory-item';

interface TerritoriesByStateProps {
  loading: boolean;
  onTerritoryClick?: (territory: Territory) => void;
}
const TerritoriesByState = ({
  loading,
  onTerritoryClick,
}: TerritoriesByStateProps) => {
  const { user } = useUser();
  const { territories, focusedTerritory } = useTerritoryStore();

  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (focusedTerritory) {
      setExpandedStates((prev) => ({
        ...prev,
        [focusedTerritory.state]: true, // 👈 explicitly open it
      }));
    }
  }, [focusedTerritory]);

  if (!territories) return null;

  // Define priority mapping
  const statusOrder: Record<string, number> = {
    sold: 1,
    reserved: 2,
    pending: 3,
    liked: 4,
    available: 5,
  };

  // Helper to resolve effective status
  const getEffectiveStatus = (territory: Territory): string => {
    if (territory.is_liked) return 'liked';
    return territory.status.toLowerCase(); // available, pending, sold
  };

  // Group territories by state
  const territoriesByState = territories.reduce(
    (acc, territory) => {
      const state = territory.state;

      if (!acc[state]) {
        acc[state] = [];
      }

      acc[state].push(territory);

      // Always sort after pushing (or sort later after reduce)
      acc[state].sort((a, b) => {
        // Determine user ownership + priority
        const getUserPriority = (t: Territory) => {
          if (t.requested_by?.id === user?.id && t.status === 'PENDING')
            return 1;
          if (t.assigned_to?.id === user?.id && t.status === 'RESERVED')
            return 2;
          return 3; // all others
        };

        const priorityA = getUserPriority(a);
        const priorityB = getUserPriority(b);

        if (priorityA !== priorityB) return priorityA - priorityB;

        // fallback to status order for remaining
        const statusA = getEffectiveStatus(a);
        const statusB = getEffectiveStatus(b);

        return statusOrder[statusA] - statusOrder[statusB];
      });

      return acc;
    },
    {} as Record<string, Territory[]>
  );

  // Calculate counts for each state
  const stateStats = Object.entries(territoriesByState).map(
    ([state, stateTerritories]) => {
      const total = stateTerritories.length;
      const sold = stateTerritories.filter((t) => t.status === 'SOLD').length;
      const reserved = stateTerritories.filter(
        (t) => t.status === 'RESERVED'
      ).length;
      const pending = stateTerritories.filter(
        (t) => t.status === 'PENDING'
      ).length;

      const available = stateTerritories.filter(
        (t) => t.status === 'AVAILABLE'
      ).length;
      const state_preference_count =
        stateTerritories[0]?.state_preference_count || 0;
      // Extract photo arrays
      const photosFromState =
        stateTerritories[0]?.state_preference_photos ?? [];
      const userPhotos = SAMPLE_ZEE_USERS.map((user) => user.photo_url);

      // Merge and shuffle
      const mergedPhotos = [...photosFromState, ...userPhotos];

      // Take first 3
      const state_preference_photos = mergedPhotos.slice(0, 3);

      return {
        state,
        total,
        sold,
        reserved,
        pending,
        available,
        state_preference_count,
        state_preference_photos,
      };
    }
  );

  const toggleState = (state: string) => {
    setExpandedStates((prev) => ({
      ...prev,
      [state]: !prev[state],
    }));
  };

  const handleToggleState = (state: string) => () => {
    toggleState(state);
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex w-full justify-center py-5">
          <Loader2 className="size-4 animate-spin" />
        </div>
      ) : (
        stateStats.map(
          ({
            state,
            total,
            sold,
            reserved,
            pending,
            state_preference_count,
            state_preference_photos,
          }) => (
            <div
              key={state}
              className="overflow-hidden rounded-xl border border-border"
            >
              <div
                className={cn(
                  'flex flex-col items-start gap-6 bg-zinc-50 px-6 py-3.5 max-lg:gap-3 max-lg:p-3 lg:flex-row lg:items-center',
                  expandedStates[state] && 'bg-brand-50'
                )}
              >
                <div className="flex items-center gap-2">
                  <MapPin
                    className={cn(
                      'h-5 w-5 text-zinc-400',
                      expandedStates[state] && 'text-brand-500'
                    )}
                  />
                  <p className="as-title-01 as-strong text-gray-700">
                    {US_STATES[state as keyof typeof US_STATES]}
                  </p>
                </div>
                <div className="flex w-full flex-col gap-3 lg:ml-auto lg:w-auto lg:flex-row lg:items-center">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 rounded-full border border-border px-4 py-2">
                      {state_preference_photos?.map((item, index) => (
                        <Avatar
                          key={index}
                          className={cn('h-6 w-6', index !== 0 && 'ml-[-20px]')}
                        >
                          <AvatarImage src={item} />
                          <AvatarFallback>
                            <UserIcon className="h-3 w-3" />
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      <p className="as-strong as-title-03 max-md:text-xs">
                        {getStateUserCount(state, state_preference_count)} other
                        users are looking in{' '}
                        {US_STATES[state as keyof typeof US_STATES]}
                      </p>
                    </div>
                    <StateStatusBadge count={total} label="territories" />
                    <StateStatusBadge count={sold} label="sold" />
                    <StateStatusBadge count={reserved} label="reserved" />
                    <StateStatusBadge count={pending} label="pending" />
                  </div>
                  <Button
                    type="button"
                    onClick={handleToggleState(state)}
                    className={cn(
                      TERRITORIES_TOUR_CLASSES.STATE_VIEW_BUTTON,
                      'max-lg:w-full'
                    )}
                  >
                    {expandedStates[state] ? 'Hide' : 'View'}
                  </Button>
                </div>
              </div>
              {expandedStates[state] && (
                <>
                  {territoriesByState[state].map((territory) => (
                    <TerritoryItem
                      key={territory.id}
                      territory={territory}
                      isOwnedByUser={
                        user?.id == territory.assigned_to?.id ||
                        user?.id == territory.requested_by?.id
                      }
                      onTerritoryClick={onTerritoryClick}
                    />
                  ))}
                </>
              )}
            </div>
          )
        )
      )}
    </div>
  );
};

export { TerritoriesByState };
